// Market pricing primitives (frontend).
//
// MIRROR OF: functions/api/util/market_pricing.js
// The backend is the billing source of truth — it computes what Stripe
// actually charges. This module exists only so the signup/billing screens
// display the same number, and must be kept in sync with its mirror.
//
// NOTE: changing MARKET_PRICE_FLOOR also requires updating the
// customer-facing copy that states the minimum in prose:
//   src/views/owner/sign_up/agent.vue:205
//   src/views/owner/sign_up/billing_coming_soon.vue:76, :373
//   src/views/owner/sign_up/market_selection.vue:50
// (:373 also names the break-even population, i.e. floor * 1000.)

/**
 * Public-facing monthly minimum, in whole USD. An agent can't claim a market
 * priced below this — it's a sales/UX rule layered on top of the raw
 * per-resident formula, not part of the formula itself.
 */
export const MARKET_PRICE_FLOOR = 295;

/**
 * Raw pricing rule: $1 per 1,000 residents, rounded up so a 293,056-resident
 * market lands at $294/mo. Deliberately UNFLOORED — the super-admin Markets
 * panel and `billing_info.monthly_price_calculated` both surface this value
 * so the actual per-1k math stays visible even when billing happens at the
 * floor. Use `floored_market_price` for anything a customer is quoted.
 */
export function calculated_market_price(population) {
  if (!population || population <= 0) return 0;
  return Math.ceil(population / 1000);
}

/**
 * The price a customer is quoted and charged: the per-1k formula raised to
 * MARKET_PRICE_FLOOR.
 *
 * Returns 0 for a non-positive population rather than the floor, because an
 * empty/undrawn market has no price to show yet — callers that want to quote
 * the floor as a starting price should do so explicitly.
 */
export function floored_market_price(population) {
  const calculated = calculated_market_price(population);
  if (calculated <= 0) return 0;
  return Math.max(MARKET_PRICE_FLOOR, calculated);
}
